import {
	Dialog,
	type DialogProps,
	IconButton,
	Input,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
	toast,
} from "@overmap-ai/blocks"
import { APIError } from "@overmap-ai/core"
import React, { forwardRef, memo, type PropsWithoutRef, useCallback, useEffect, useState } from "react"

import { emailRegex } from "../../constants"
import { modelIconMapping } from "../../mappings/icons"

interface EmailVerificationModalProps extends Omit<PropsWithoutRef<DialogProps>, "content"> {
	email: string
	onEmailChange: (email: string) => void
	error: string
	onErrorChange: (error: string) => void
	onConfirm: () => Promise<undefined>
	onSuccess: () => void
	emailsToFilterOut?: string[]
	emailToFilterOutErrorMessage?: string
	title: string
	description?: string
}

export const EmailVerificationModal = memo(
	forwardRef<HTMLDivElement, EmailVerificationModalProps>((props, ref) => {
		const {
			children,
			title,
			description,
			email,
			onEmailChange,
			error,
			onErrorChange,
			onConfirm,
			onSuccess,
			emailsToFilterOut,
			emailToFilterOutErrorMessage = "This email is already in use.",
			...rest
		} = props
		const [loading, setLoading] = useState(false)
		const isValid = emailRegex.test(email)

		useEffect(() => {
			if (isValid && emailsToFilterOut) {
				const emailExists = emailsToFilterOut.find(
					(filterValue: string) => filterValue.toLowerCase() === email.toLowerCase(),
				)
				onErrorChange(emailExists ? emailToFilterOutErrorMessage : "")
			} else {
				onErrorChange("")
			}
		}, [email, emailToFilterOutErrorMessage, emailsToFilterOut, isValid, onErrorChange])

		useEffect(() => {
			return () => {
				onEmailChange("")
				onErrorChange("")
				setLoading(false)
			}
		}, [onEmailChange, onErrorChange])

		const invite = useCallback(async () => {
			setLoading(true)
			onConfirm()
				.then(() => {
					setLoading(false)
					onSuccess()
				})
				.catch((error: unknown) => {
					setLoading(false)
					onEmailChange("")

					if (error instanceof APIError) {
						toast.error({
							title: "The server rejected the request",
							description: error.message ?? "An unknown error occurred.",
						})
					} else {
						toast.error({ title: "An unknown error occurred", description: "" })
					}
				})
		}, [onConfirm, onSuccess, onEmailChange])

		const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
			(e) => {
				e.preventDefault()
				void invite()
			},
			[invite],
		)
		const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
			(e) => {
				onEmailChange(e.target.value)
			},
			[onEmailChange],
		)
		return (
			<Dialog.Root {...rest}>
				<Dialog.Trigger asChild>{children}</Dialog.Trigger>
				<Dialog.Overlay onClick={stopPropagation} />
				<Dialog.Content ref={ref} onClick={stopPropagation} size="sm">
					{(close) => (
						<>
							<div className="flex justify-between gap-2">
								<Dialog.Title>
									<Text size="lg">{title}</Text>
								</Dialog.Title>

								<Dialog.Close asChild>
									<IconButton variant="ghost" accentColor="base">
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								</Dialog.Close>
							</div>

							<div className="flex w-full flex-col gap-2">
								{!!description && (
									<>
										<Text aria-label="input-description" size="sm" accentColor="base">
											{description}
										</Text>
										<Separator size="full" />
									</>
								)}

								<form onSubmit={handleSubmit} className="flex flex-col gap-1">
									<Text size="sm" accentColor="base">
										Email address
									</Text>

									<div className="flex gap-2">
										<Input.Root className="grow" variant="outline">
											<Input.Field
												id="email-input"
												value={email}
												onChange={handleEmailChange}
												disabled={loading}
											/>
										</Input.Root>

										<IconButton
											onClick={() => close()}
											type="submit"
											disabled={loading || !isValid || !!error}
											variant="surface"
											aria-label="submit"
										>
											<LuIcon icon={modelIconMapping.check} />
										</IconButton>
									</div>

									<Text accentColor="red" size="xs">
										{error}
									</Text>
								</form>
							</div>
						</>
					)}
				</Dialog.Content>
			</Dialog.Root>
		)
	}),
)

EmailVerificationModal.displayName = "EmailVerificationModal"
